<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    LoadingState,
    SettingsSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      supabaseTables: [],
      inactivityTransferWebhookUrl: '',
      inactivityTransferThresholdMinutes: 30,
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](this.inbox.id);
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
    },
    inbox: {
      immediate: true,
      handler(inbox) {
        this.supabaseTables = (inbox?.supabase_config?.tables || []).map(t => ({
          name: t.name || '',
          description: t.description || '',
        }));
        this.inactivityTransferWebhookUrl =
          inbox?.inactivity_transfer_config?.webhook_url || '';
        this.inactivityTransferThresholdMinutes =
          inbox?.inactivity_transfer_config?.threshold_minutes || 30;
      },
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
  },

  methods: {
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
    addSupabaseTable() {
      this.supabaseTables.push({ name: '', description: '' });
    },
    removeSupabaseTable(index) {
      this.supabaseTables.splice(index, 1);
    },
    async saveSupabaseTables() {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          supabase_config: {
            tables: this.supabaseTables.filter(t => t.name.trim()),
          },
        });
        useAlert(this.$t('INBOX_MGMT.SUPABASE.SUCCESS'));
      } catch {
        useAlert(this.$t('INBOX_MGMT.SUPABASE.ERROR'));
      }
    },
    async saveInactivityTransferConfig() {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          inactivity_transfer_config: {
            webhook_url: this.inactivityTransferWebhookUrl,
            threshold_minutes: this.inactivityTransferThresholdMinutes,
          },
        });
        useAlert(this.$t('INBOX_MGMT.INACTIVITY_TRANSFER.SUCCESS'));
      } catch {
        useAlert(this.$t('INBOX_MGMT.INACTIVITY_TRANSFER.ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <template v-else>
      <!-- Bot Configuration -->
      <form class="flex flex-wrap mx-0" @submit.prevent="updateActiveAgentBot">
        <SettingsSection
          :title="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
          :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
        >
          <div>
            <label>
              <select v-model="selectedAgentBotId">
                <option value="" disabled selected>
                  {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER') }}
                </option>
                <option
                  v-for="agentBot in agentBots"
                  :key="agentBot.id"
                  :value="agentBot.id"
                >
                  {{ agentBot.name }}
                </option>
              </select>
            </label>
            <div class="button-container space-x-2">
              <NextButton
                type="submit"
                :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
                :is-loading="uiFlags.isSettingAgentBot"
              />
              <NextButton
                type="button"
                :disabled="!selectedAgentBotId"
                :is-loading="uiFlags.isDisconnecting"
                faded
                ruby
                @click="disconnectBot"
              >
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
              </NextButton>
            </div>
          </div>
        </SettingsSection>
      </form>

      <!-- Inactivity Transfer Configuration -->
      <SettingsSection
        :title="$t('INBOX_MGMT.INACTIVITY_TRANSFER.TITLE')"
        :sub-title="$t('INBOX_MGMT.INACTIVITY_TRANSFER.DESC')"
      >
        <div class="space-y-3">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('INBOX_MGMT.INACTIVITY_TRANSFER.WEBHOOK_URL_LABEL') }}
            </label>
            <input
              v-model="inactivityTransferWebhookUrl"
              type="url"
              :placeholder="
                $t('INBOX_MGMT.INACTIVITY_TRANSFER.WEBHOOK_URL_PLACEHOLDER')
              "
              class="w-full rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('INBOX_MGMT.INACTIVITY_TRANSFER.THRESHOLD_LABEL') }}
            </label>
            <input
              v-model.number="inactivityTransferThresholdMinutes"
              type="number"
              min="1"
              :placeholder="
                $t('INBOX_MGMT.INACTIVITY_TRANSFER.THRESHOLD_PLACEHOLDER')
              "
              class="w-full rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
            />
          </div>
          <div class="pt-1">
            <NextButton
              size="sm"
              :label="$t('INBOX_MGMT.INACTIVITY_TRANSFER.SAVE')"
              @click="saveInactivityTransferConfig"
            />
          </div>
        </div>
      </SettingsSection>

      <!-- Supabase Tables Configuration -->
      <SettingsSection
        :title="$t('INBOX_MGMT.SUPABASE.TITLE')"
        :sub-title="$t('INBOX_MGMT.SUPABASE.DESC')"
      >
        <div class="space-y-3">
          <div
            v-for="(table, index) in supabaseTables"
            :key="index"
            class="flex items-start gap-3 rounded-xl border border-n-alpha-3 bg-n-alpha-1 p-3"
          >
            <div class="flex-1 space-y-2">
              <input
                v-model="table.name"
                type="text"
                :placeholder="$t('INBOX_MGMT.SUPABASE.TABLE_NAME_PLACEHOLDER')"
                class="w-full rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
              <input
                v-model="table.description"
                type="text"
                :placeholder="$t('INBOX_MGMT.SUPABASE.TABLE_DESC_PLACEHOLDER')"
                class="w-full rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-11 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>
            <button
              type="button"
              class="mt-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-n-ruby-5 text-n-ruby-9 transition hover:bg-n-ruby-3"
              @click="removeSupabaseTable(index)"
            >
              <span class="i-lucide-trash-2 text-base" />
            </button>
          </div>

          <div
            v-if="!supabaseTables.length"
            class="rounded-xl border border-dashed border-n-alpha-3 py-6 text-center text-sm text-n-slate-10"
          >
            {{ $t('INBOX_MGMT.SUPABASE.EMPTY') }}
          </div>

          <div class="flex items-center gap-2 pt-1">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg border border-n-alpha-3 px-3 py-1.5 text-sm text-n-slate-11 transition hover:border-n-alpha-5 hover:text-n-slate-12"
              @click="addSupabaseTable"
            >
              <span class="i-lucide-plus text-base" />
              {{ $t('INBOX_MGMT.SUPABASE.ADD_TABLE') }}
            </button>
            <NextButton
              size="sm"
              :label="$t('INBOX_MGMT.SUPABASE.SAVE')"
              @click="saveSupabaseTables"
            />
          </div>
        </div>
      </SettingsSection>
    </template>
  </div>
</template>
